// GP Calculation Utility — Task 23
//
// All Gross Profit / Gross Margin calculations across the Financials tab must
// call calculateGP() so that the company-wide GP Calculation Method setting
// (stored in finance-settings-storage) is respected uniformly.
//
// Three methods are supported:
//   simple        — Revenue minus ALL direct costs (default)
//   contribution  — Revenue minus Labour and Subcontractors only
//   fully-loaded  — Revenue minus direct costs plus overhead absorption
//
// Phase 1: method is read from localStorage via getFinanceSettings().
// Phase 2: replace storage call with a GET /api/settings/finance call.

export type GpCalculationMethod = 'simple' | 'contribution' | 'fully-loaded';

/**
 * Inputs for a GP calculation.
 * Fields that are irrelevant to a given method can be omitted (default 0).
 */
export interface GpCalculationInputs {
  /** Total revenue recognised / actual sales */
  revenue: number;

  /** Total direct costs (labour + expenses + subcontractors + materials + equipment + other) */
  totalDirectCosts: number;

  /**
   * Labour cost only (used by 'contribution' method).
   * Defaults to 0 if not provided.
   */
  labourCosts?: number;

  /**
   * Subcontractor cost only (used by 'contribution' method).
   * Defaults to 0 if not provided.
   */
  subcontractorCosts?: number;

  /**
   * Overhead absorption amount (used by 'fully-loaded' method).
   * Defaults to 0 if not provided.
   */
  overheadAbsorption?: number;
}

/**
 * Result of a GP calculation.
 */
export interface GpCalculationResult {
  /** Gross profit in currency units */
  grossProfit: number;
  /** Gross margin as a percentage (0–100 scale, can be negative) */
  grossMarginPct: number;
  /** The method that was used */
  method: GpCalculationMethod;
}

/**
 * Calculate Gross Profit and Gross Margin % using the specified method.
 *
 * @param inputs  Cost/revenue inputs
 * @param method  GP calculation method — defaults to 'simple'
 * @returns       { grossProfit, grossMarginPct, method }
 *
 * @example
 * // Simple margin
 * calculateGP({ revenue: 100_000, totalDirectCosts: 70_000 }, 'simple')
 * // → { grossProfit: 30_000, grossMarginPct: 30, method: 'simple' }
 *
 * @example
 * // Contribution margin (labour + subs only)
 * calculateGP(
 *   { revenue: 100_000, totalDirectCosts: 70_000, labourCosts: 40_000, subcontractorCosts: 10_000 },
 *   'contribution'
 * )
 * // → { grossProfit: 50_000, grossMarginPct: 50, method: 'contribution' }
 */
export function calculateGP(
  inputs: GpCalculationInputs,
  method: GpCalculationMethod = 'simple'
): GpCalculationResult {
  const {
    revenue,
    totalDirectCosts,
    labourCosts = 0,
    subcontractorCosts = 0,
    overheadAbsorption = 0,
  } = inputs;

  let grossProfit: number;

  switch (method) {
    case 'contribution':
      // Contribution Margin: Revenue minus Labour and Subcontractors only
      grossProfit = revenue - labourCosts - subcontractorCosts;
      break;

    case 'fully-loaded':
      // Fully Loaded Margin: Revenue minus direct costs plus overhead absorption
      grossProfit = revenue - totalDirectCosts - overheadAbsorption;
      break;

    case 'simple':
    default:
      // Simple Margin: Revenue minus all direct costs
      grossProfit = revenue - totalDirectCosts;
      break;
  }

  const grossMarginPct = revenue > 0 ? (grossProfit / revenue) * 100 : 0;

  return { grossProfit, grossMarginPct, method };
}

/**
 * Convenience wrapper: calculate BUDGET GP using the same method.
 * Identical to calculateGP but named for clarity at call sites.
 */
export function calculateBudgetGP(
  inputs: GpCalculationInputs,
  method: GpCalculationMethod = 'simple'
): GpCalculationResult {
  return calculateGP(inputs, method);
}

/**
 * Human-readable label for each GP calculation method.
 */
export const GP_METHOD_LABELS: Record<GpCalculationMethod, string> = {
  simple: 'Simple Margin',
  contribution: 'Contribution Margin',
  'fully-loaded': 'Fully Loaded Margin',
};

/**
 * Short description for each GP calculation method (for Settings UI).
 */
export const GP_METHOD_DESCRIPTIONS: Record<GpCalculationMethod, string> = {
  simple: 'Revenue minus all direct costs. The most common method — includes labour, subcontractors, expenses, materials, and equipment.',
  contribution: 'Revenue minus Labour and Subcontractors only. Useful for understanding the core delivery margin before other direct costs.',
  'fully-loaded': 'Revenue minus direct costs plus an overhead absorption charge. Used when overhead recovery is tracked at project level.',
};
